import React, { useState } from "react";
import {
  Box, Button, TextField, Typography,
  CircularProgress, Alert, GlobalStyles, InputAdornment, IconButton,
} from "@mui/material";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import axiosInstance from "../../../services/axiosInstance";
import logoImg  from "../../../../design-system/assets/brand/logo.png";
import teliWave from "../../../../design-system/assets/mascot/teli-wave.png";
import bgSky    from "../../../../design-system/assets/bg-sky.png";
import PasswordRequirements from "../../common/PasswordRequirements";
import { isPasswordValid } from "../../../utils/passwordPolicy";
import { LanguageSwitcherCompact } from "../../../i18n/LanguageSwitcher.jsx";
import { useTranslation } from "react-i18next";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    height: 44, borderRadius: "var(--r-md)", backgroundColor: "#fff",
    fontFamily: "var(--font-ui)",
    "& input": { fontFamily: "var(--font-ui)", fontSize: "var(--t-body)", color: "var(--ink-800)", padding: "0 14px" },
    "& fieldset": { borderColor: "var(--line-200)" },
    "&:hover fieldset": { borderColor: "var(--line-300)" },
    "&.Mui-focused fieldset": { borderColor: "var(--line-200)", borderWidth: "1px" },
  },
  "& .MuiFormHelperText-root": { fontFamily: "var(--font-ui)", fontSize: "var(--t-caption)", marginInline: 0 },
};

const btnSx = {
  height: 48, borderRadius: "var(--r-md)",
  background: "var(--grad-brand-135)",
  boxShadow: "0 8px 20px rgba(90,70,220,.28)",
  fontFamily: "var(--font-ui)", fontWeight: 600, fontSize: "var(--t-body)",
  color: "#fff", textTransform: "none", border: "none",
  "&:hover": { filter: "brightness(1.06)" },
  "&:active": { transform: "translateY(1px)", transition: "transform .1s" },
  "&.Mui-disabled": { background: "var(--grad-brand-135)", color: "rgba(255,255,255,.5)" },
};

export default function AcceptInvitation() {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [password, setPassword]       = useState("");
  const [confirm, setConfirm]         = useState("");
  const [showPwd, setShowPwd]         = useState(false);
  const [showCnf, setShowCnf]         = useState(false);
  const [error, setError]             = useState("");
  const [loading, setLoading]         = useState(false);
  const [done, setDone]               = useState(false);
  const [tokenBad, setTokenBad]       = useState(!token);

  const handleSubmit = async () => {
    if (!isPasswordValid(password)) { setError(t("errMeetRequirements")); return; }
    if (password !== confirm)       { setError(t("errPasswordsMatch")); return; }
    setError(""); setLoading(true);
    try {
      await axiosInstance.post("/auth/accept-invitation", { invitationToken: token, password });
      setDone(true);
    } catch (e) {
      const msg = e.response?.data?.message || t("errVerificationFailed");
      if (msg.toLowerCase().includes("expired") || msg.toLowerCase().includes("invalid")) setTokenBad(true);
      setError(msg);
    } finally { setLoading(false); }
  };

  return (
    <>
    <GlobalStyles styles={`
      @keyframes teliFloat { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
      @media (prefers-reduced-motion: reduce) { .teli-float { animation: none !important; } }
    `} />
    <Box sx={{
      display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
      minHeight: "100dvh", position: "relative", overflow: "hidden",
      backgroundImage: `url(${bgSky})`, backgroundSize: "cover",
      backgroundPosition: "center top", backgroundRepeat: "no-repeat",
    }}>
      {/* Language switcher — top-right corner of the full-page layout */}
      <Box sx={{ position: "absolute", top: 16, insetInlineEnd: 16, zIndex: 10 }}>
        <LanguageSwitcherCompact variant="light" />
      </Box>

      {/* LEFT PANEL */}
      <Box sx={{
        display: "grid", placeItems: "center end",
        padding: { xs: "24px", sm: "40px 24px 40px 40px" },
        overflowY: "auto", alignContent: "center",
        position: "relative", zIndex: 1,
      }}>
        <Box sx={{
          width: "100%", maxWidth: 420,
          background: "rgba(255,255,255,.96)",
          borderRadius: "var(--r-xl)",
          boxShadow: "0 16px 48px rgba(46,55,120,.14)",
          padding: "40px",
        }}>
          <Box component="img" src={logoImg} alt="Teleredo" role="button" tabIndex={0}
            sx={{ height: 34, width: "auto", mb: "24px", display: "block", cursor: "pointer" }}
            onClick={() => navigate("/")} onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && navigate("/")} />

          {/* Invalid / expired token */}
          {tokenBad && !done && (
            <>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
                <Box sx={{ width: 44, height: 44, borderRadius: "12px", flexShrink: 0, background: "rgba(220,38,38,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <ErrorOutlineIcon sx={{ fontSize: 22, color: "#DC2626" }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "1.25rem", fontWeight: 800, color: "var(--ink-900)", letterSpacing: "-0.01em" }}>
                    {t("invitationExpired")}
                  </Typography>
                  <Typography sx={{ fontSize: "0.8125rem", color: "var(--ink-500)", lineHeight: 1.4 }}>
                    {t("invitationNoLongerValid")}
                  </Typography>
                </Box>
              </Box>
              <Typography sx={{ fontSize: "0.875rem", color: "var(--ink-600)", mb: 3, lineHeight: 1.6 }}>
                {t("invitationExpiredDesc")}
              </Typography>
              <Button fullWidth variant="contained" onClick={() => navigate("/signin")} sx={btnSx}>
                {t("backToSignIn")}
              </Button>
            </>
          )}

          {/* Set password form */}
          {!tokenBad && !done && (
            <>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
                <Box sx={{ width: 44, height: 44, borderRadius: "12px", flexShrink: 0, background: "rgba(79,70,229,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <LockOutlinedIcon sx={{ fontSize: 22, color: "#4F46E5" }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "1.25rem", fontWeight: 800, color: "var(--ink-900)", letterSpacing: "-0.01em" }}>
                    {t("setPassword")}
                  </Typography>
                  <Typography sx={{ fontSize: "0.8125rem", color: "var(--ink-500)", lineHeight: 1.4 }}>
                    {t("chooseStrongPassword")}
                  </Typography>
                </Box>
              </Box>

              {error && (
                <Alert severity="error" variant="outlined" onClose={() => setError("")}
                  sx={{ mb: 2, borderRadius: "var(--r-md)", fontSize: "var(--t-caption)" }}>
                  {error}
                </Alert>
              )}

              <Box sx={{ mb: "14px" }}>
                <Typography component="label" htmlFor="password" sx={{ display: "block", fontSize: "var(--t-caption)", fontWeight: 600, color: "var(--ink-600)", mb: "6px" }}>
                  {t("password")}
                </Typography>
                <TextField fullWidth hiddenLabel id="password" name="password"
                  type={showPwd ? "text" : "password"} autoComplete="new-password"
                  placeholder={t("minCharacters")} autoFocus
                  value={password} onChange={(e) => setPassword(e.target.value)} sx={inputSx}
                  InputProps={{ endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowPwd(p => !p)} edge="end" tabIndex={-1}
                        aria-label={showPwd ? t("hidePassword") : t("showPassword")}
                        sx={{ color: "var(--ink-400)", mr: "-4px" }}>
                        {showPwd ? <VisibilityOffOutlinedIcon sx={{ fontSize: 18 }} /> : <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />}
                      </IconButton>
                    </InputAdornment>
                  ) }} />
                <PasswordRequirements password={password} />
              </Box>

              <Box sx={{ mb: "20px" }}>
                <Typography component="label" htmlFor="confirm" sx={{ display: "block", fontSize: "var(--t-caption)", fontWeight: 600, color: "var(--ink-600)", mb: "6px" }}>
                  {t("confirmPassword")}
                </Typography>
                <TextField fullWidth hiddenLabel id="confirm" name="confirm"
                  type={showCnf ? "text" : "password"} autoComplete="new-password"
                  placeholder={t("repeatPassword")}
                  value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSubmit()} sx={inputSx}
                  InputProps={{ endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowCnf(p => !p)} edge="end" tabIndex={-1}
                        aria-label={showCnf ? t("hidePassword") : t("showPassword")}
                        sx={{ color: "var(--ink-400)", mr: "-4px" }}>
                        {showCnf ? <VisibilityOffOutlinedIcon sx={{ fontSize: 18 }} /> : <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />}
                      </IconButton>
                    </InputAdornment>
                  ) }} />
              </Box>

              <Button fullWidth variant="contained"
                disabled={loading || !isPasswordValid(password) || password !== confirm}
                onClick={handleSubmit} sx={btnSx}>
                {loading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : t("activateAccount")}
              </Button>
            </>
          )}

          {/* Success */}
          {done && (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", py: 2 }}>
              <Box sx={{ width: 56, height: 56, borderRadius: "50%", mb: 2, background: "rgba(21,128,61,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 30, color: "#15803D" }} />
              </Box>
              <Typography sx={{ fontSize: "1.25rem", fontWeight: 800, color: "var(--ink-900)", mb: 0.5 }}>
                {t("accountActivated")}
              </Typography>
              <Typography sx={{ fontSize: "0.875rem", color: "var(--ink-500)", mb: 3, lineHeight: 1.6 }}>
                {t("passwordAccountReady")}
              </Typography>
              <Button fullWidth variant="contained" onClick={() => navigate("/signin")} sx={btnSx}>
                {t("signIn")}
              </Button>
            </Box>
          )}

        </Box>
      </Box>

      {/* RIGHT PANEL */}
      <Box sx={{
        display: { xs: "none", md: "grid" }, placeItems: "end start",
        paddingLeft: "16px", overflow: "hidden", position: "relative", zIndex: 1,
      }}>
        <Box sx={{
          position: "absolute", top: "15%", left: "50%",
          transform: "translateX(-50%)", width: 560, height: 560, borderRadius: "50%",
          background: "radial-gradient(closest-side, rgba(122,63,228,0.45), transparent)",
          filter: "blur(90px)", opacity: 0.18, pointerEvents: "none", zIndex: 0,
        }} />
        <Box component="img" src={teliWave} alt="Teli, your Teleredo AI assistant"
          className="teli-float"
          sx={{
            position: "relative", zIndex: 2,
            height: "88vh", width: "auto", display: "block",
            filter: "drop-shadow(0 22px 34px rgba(46,55,120,.16))",
            animation: "teliFloat var(--float-loop, 6s) ease-in-out infinite",
          }} />
      </Box>
    </Box>
    </>
  );
}
